import React from 'react';
import Image from 'next/image';
import styles from './index.module.less';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { message, Row, Col } from 'antd';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useRouter } from 'next/navigation';

interface SearchReportItem {
  task_id: number;
  status: string;
  payment_url: null;
  search_image: string;
  email_notify: boolean;
  type: string;
  create_time: string;
  result_count: number;
}

interface ImageSearchReportProps {
  reportList: any[];
  reportListLoading: boolean;
  contentFunction: string;
}

// eslint-disable-next-line
export default function ImageSearchReport({ reportList, reportListLoading, contentFunction }: ImageSearchReportProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const handleCardClick = (report: SearchReportItem) => {
    if (report.status === 'processing') {
      message.info(t('Please wait for the report to be completed'));
    } else {
      // 跳转到报告详情
      router.push(`/dashboard/privacy-protection/portrait?reportId=${report.task_id}`);
    }
  };

  if (reportListLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>{t('My Search Reports')}</div>
        </div>
        <Row gutter={[16, 16]} className={styles.reportList}>
          {[1, 2, 3, 4].map((i) => (
            <Col key={i} xs={12} lg={6}>
              <div className={styles.skeletonCard}>
                <div className={styles.skeletonImage} />
                <div className={styles.skeletonInfo}>
                  <div className={styles.skeletonText} />
                  <div className={styles.skeletonTextShort} />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (!reportList || reportList.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('My Search Reports')}</div>
        <span className={styles.viewAll} onClick={() => router.push('/dashboard/privacy-protection/portrait')}>
          {t(`Dashboard`)} {` >`}
        </span>
      </div>

      <Row gutter={[16, 16]} className={styles.reportList}>
        {reportList.slice(0, 4).map((report: SearchReportItem) => (
          <Col key={report.task_id} xs={12} lg={6}>
            <div className={styles.reportCard} onClick={() => handleCardClick(report)}>
              <div className={styles.imageWrapper}>
                <Image
                  src={report.search_image}
                  alt="Search report"
                  className={styles.reportImage}
                  fill
                  sizes="(max-width: 992px) 50vw, 25vw"
                />
                {report.status === 'processing' && (
                  <div className={styles.searchingOverlay}>
                    <div>
                      <Loader2 className={styles.searchingIcon} size={isMobile ? 24 : 50} />
                      <span className={styles.searchingText}>{t('Searching and Analyzing...')}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.reportInfo}>
                <div className={styles.resultsCount}>
                  {report.status === 'processing' ? (
                    <span>🔐*** {t('Results Found')}</span>
                  ) : (
                    <span>
                      {formatResultsCount(report.result_count)} {t('Results Found')}
                    </span>
                  )}
                </div>
                <div className={styles.date}>{formatDate(report.create_time)}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatResultsCount = (count: number) => {
  return count ? count.toLocaleString() : 0;
};
